import { createSlice } from "@reduxjs/toolkit";
import {
  convertISODateTime,
  convertDefaultTimeZoneStringToISO,
  roundToInterval,
} from "@utils/time-helpers";
import moment from "moment-timezone";

const initialState = {
  saveEnabled: true,
  offer_page: true,
  retailer_product_id: "",
  grpid: "",
  name: "",
  start_date: "",
  stop_date: "",
  inputISOStartDate: null,
  inputISOStopDate: null,
  original_start_date: null,
  original_stop_date: null,
  file_name: "",
  offer_type: "",
  coupon_code: "",
  short_description: "",
  locations: [],
  image_data: "",
  external_url: "",
  supplemental: {},
  blob: "",
  segment_ids: [],
  long_description: "",
  promotionLoaded: false,
  errors: {},
  segments: [],
  validInputs: false,
  showDropZoneNew: false,
  image_url: "",
  script_type: "",
  script_value: "",
  carouselBlob: "",
  carousel_image_url: "",
  carousel_image_data: "",
  carouselmageName: "",
  carousel_label: "",
};

const promotionNewViewSlice = createSlice({
  name: "promotionNewView",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase("LOAD_PROMOTION", (state, action) => {
        Object.assign(state, action.payload);
        state.original_start_date = action.payload.start_date || null;
        state.original_stop_date = action.payload.stop_date || null;
        state.supplementalDataLoaded = true;
        state.promotionLoaded = true;
      })
      .addCase("SET_CAROUSEL_LABEL", (state, action) => {
        state.carousel_label = action.carouselLabel;
      })
      .addCase("SET_OFFER_TYPE", (state, action) => {
        state.offer_type = action.offerType;
      })
      .addCase("SET_COUPON_CODE", (state, action) => {
        state.coupon_code = action.offerValue;
      })
      .addCase("SET_OFFER_PAGE", (state, action) => {
        state.offer_page = action.visibility;
      })
      .addCase("SET_DESCRIPTION", (state, action) => {
        state.short_description = action.description;
      })
      .addCase("SET_LONG_DESCRIPTION", (state, action) => {
        state.long_description = action.longDescription;
      })
      .addCase("DISABLE_SAVE", (state) => {
        state.saveEnabled = false;
      })
      .addCase("ENABLE_SAVE", (state) => {
        state.saveEnabled = true;
      })
      .addCase("SET_LOCATIONS", (state, action) => {
        state.locations = action.locations;
      })
      .addCase("SET_RETAILER_PRODUCT_ID", (state, action) => {
        state.retailer_product_id = action.retailerProductId;
      })
      .addCase("UPDATE_PROMOTION", (state, action) => {
        const { offer_type, start_date, stop_date, coupon_code, sku } =
          action.promotion || {};
        const convert = {
          Coupon1D: "Coupon1D",
          Coupon2D: "Coupon2D",
          Offer: "Offer",
        };
        const offer_type_value = convert[offer_type] || "Offer";
        state.payload = {
          ...(state.payload || {}),
          ...action.promotion,
          inputISOStartDate: start_date,
          inputISOStopDate: stop_date,
          offer_type: offer_type_value,
          coupon_code: coupon_code || sku,
        };
        state.promotionLoaded = true;
      })
      .addCase("ENABLE_POST", (state, action) => {
        state.validInputs = !action.hasErrors;
      })
      .addCase("REMOVE_FILE", (state) => {
        state.blob = "";
        state.image_data = "";
        state.file_name = "";
        state.image_url = "";
      })
      .addCase("REMOVE_CAROUSEL_IMAGE", (state) => {
        state.carouselBlob = "";
        state.carousel_image_data = "";
        state.carouselmageName = "";
        state.carousel_image_url = "";
      })
      .addCase("RESET_FORM_FIELDS", () => ({
        ...initialState,
        promotionLoaded: false,
      }))
      .addCase("SET_TITLE", (state, action) => {
        state.name = action.title;
      })
      .addCase("SET_NEW_DATE", (state, action) => {
        let timeString,
          combinedString,
          newISODateTime = null,
          dateToSet;
        if (action.dateString) {
          dateToSet =
            action.whatToSet === "startDate"
              ? state.inputISOStartDate
              : state.inputISOStopDate;
          timeString = dateToSet
            ? dateToSet
            : moment.tz("America/New_York").toISOString();
          timeString = convertISODateTime(
            roundToInterval(timeString, 1)
          ).toMomentWithConsoleConfigTimeZone.format("h:mmA");
          combinedString = action.dateString + timeString;
          newISODateTime = convertDefaultTimeZoneStringToISO(combinedString);
        }
        if (action.whatToSet === "startDate") {
          state.inputISOStartDate = newISODateTime;
        } else {
          state.inputISOStopDate = newISODateTime;
        }
      })
      .addCase("SET_EXTERNAL_URL", (state, action) => {
        state.external_url = action.extUrl;
      })
      .addCase("SET_NEW_TIME", (state, action) => {
        let dateString, combinedString, newISODateTime, dateToSet;
        dateToSet =
          action.whatToSet === "startTime"
            ? state.inputISOStartDate
            : state.inputISOStopDate;
        dateString =
          convertISODateTime(dateToSet).toMomentWithConsoleConfigTimeZone.format(
            "MM/DD/YYYY"
          );
        combinedString = dateString + action.timeString;
        newISODateTime = convertDefaultTimeZoneStringToISO(combinedString);
        if (action.whatToSet === "startTime") {
          state.inputISOStartDate = newISODateTime;
        } else {
          state.inputISOStopDate = newISODateTime;
        }
      })
      .addCase("SET_USER_SEGMENT", (state, action) => {
        let modifiedSegments, modifiedSegmentIds;
        if (action.segment_id.length === 0) {
          modifiedSegments = [];
          modifiedSegmentIds = [];
        } else {
          modifiedSegments = state.segments.map((segment) => ({
            ...segment,
            checked:
              segment.segment_id === action.segment_id
                ? !segment.checked
                : segment.checked,
          }));
          modifiedSegmentIds = state.segment_ids.includes(action.segment_id)
            ? state.segment_ids.filter(
                (segmentId) => segmentId !== action.segment_id
              )
            : state.segment_ids.concat(action.segment_id);
        }
        state.segments = modifiedSegments;
        state.segment_ids = modifiedSegmentIds;
      })
      .addCase("SET_SUPPLEMENTAL_DATA", (state, action) => {
        state.supplemental = action.supplementalData.reduce(
          (accumulator, currentValue) =>
            currentValue.rowName
              ? { ...accumulator, [currentValue.rowName]: currentValue.rowValue }
              : accumulator,
          {}
        );
      })
      .addCase("SET_SHOW_UPLOAD_PANEL", (state, action) => {
        state.showUploadImagePanel = action.show;
      })
      .addCase("SET_SHOW_DROP_ZONE", (state, action) => {
        state.showDropZoneNew = action.show;
        state.showUploadImagePanel = false;
      })
      .addCase("CLEAR_SELECTED_IMAGE", (state) => {
        state.validInputs = false;
        state.selectedImage = { ...(initialState.selectedImage || {}) };
      })
      .addCase("DROP_FILE", (state, action) => {
        const file = action.file[0];
        state.blob = file.blob;
        state.image_url = file.preview;
        state.image_data = action.base64;
        state.file_name = file.name;
        state.showDropZoneNew = false;
      })
      .addCase("DROP_CAROUSEL_IMAGE", (state, action) => {
        const carouselImage = action.carouselImage[0];
        state.carouselBlob = carouselImage.blob;
        state.carousel_image_url = carouselImage.preview;
        state.carousel_image_data = action.base64;
        state.carouselmageName = carouselImage.name;
        state.showDropZoneNew = false;
      })
      .addCase("SET_CAROUSEL_IMAGE", (state, action) => {
        state.carouselBlob = action.blob;
        state.carousel_image_url = action.imageUrl;
        state.carousel_image_data = action.imageData;
        state.carouselmageName = action.fileName;
        state.showDropZoneNew = false;
      })
      .addCase("SET_ERROR", (state, action) => {
        if (action.error.errorName) {
          state.errors[action.error.errorName] = action.error.errorPresent;
        } else {
          Object.assign(state.errors, action.error);
        }
      })
      .addCase("SET_ACTION_TYPE", (state, action) => {
        state.script_type = action.script_type;
      })
      .addCase("SET_ACTION_VALUE", (state, action) => {
        state.script_value = action.script_value;
      });
  },
});

export const selectPromotionNewView = (state) => state.promotionNewView;

export default promotionNewViewSlice.reducer;
